import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import AdminPageWrapper from '../components/AdminPageWrapper';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import { clusterAPI } from '../services/api';

const AdminBackgroundJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [jobDetails, setJobDetails] = useState({});

  useEffect(() => {
    fetchJobs();
    // Refresh every 10 seconds
    const interval = setInterval(fetchJobs, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchJobs = async () => {
    try {
      const data = await clusterAPI.getStatus();
      setJobs(data.jobs || []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      setError('Failed to load background jobs');
    } finally {
      setLoading(false);
    }
  };

  const triggerJob = async (jobName) => {
    setActionLoading(`trigger_${jobName}`);
    setError(null);
    setSuccessMessage(null);
    try {
      const data = await clusterAPI.triggerJob(jobName);

      if (data.success) {
        setSuccessMessage(`Job ${jobName} triggered successfully`);

        // Clear cached job details to force refresh on next expand
        setJobDetails(prev => {
          const updated = { ...prev };
          delete updated[jobName];
          return updated;
        });

        // Refresh job list
        fetchJobs();

        // If job is currently expanded, refetch its details after a short delay
        // (to allow job to complete and execution history to be recorded)
        if (expandedJobs[jobName]) {
          setTimeout(async () => {
            try {
              const details = await clusterAPI.getJob(jobName);
              setJobDetails(prev => ({
                ...prev,
                [jobName]: details
              }));
            } catch (error) {
              console.error('Failed to refresh job details:', error);
            }
          }, 2000); // Wait 2 seconds for job to complete
        }
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Failed to trigger job:', error);
      setError(`Failed to trigger job ${jobName}`);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleJobExpansion = async (jobName) => {
    const isExpanded = expandedJobs[jobName];

    // Toggle expansion state
    setExpandedJobs(prev => ({
      ...prev,
      [jobName]: !isExpanded
    }));

    // Fetch job details if expanding (always fetch to ensure fresh data)
    if (!isExpanded) {
      try {
        const details = await clusterAPI.getJob(jobName);
        setJobDetails(prev => ({
          ...prev,
          [jobName]: details
        }));
      } catch (error) {
        console.error('Failed to fetch job details:', error);
        setError(`Failed to load details for job ${jobName}`);
      }
    }
  };

  const getStatusChip = (status) => {
    const statusColors = {
      'idle': 'default',
      'running': 'primary',
      'error': 'error',
      'disabled': 'default'
    };

    const statusIcons = {
      'idle': <ScheduleIcon fontSize="small" />,
      'running': <CircularProgress size={16} />,
      'error': <ErrorIcon fontSize="small" />,
      'disabled': null
    };

    return (
      <Chip
        icon={statusIcons[status]}
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={statusColors[status] || 'default'}
        size="small"
      />
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <AdminPageWrapper title="Background Jobs">
        <LoadingSpinner />
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper
      title="Background Jobs"
      subtitle="View and manage scheduled background jobs"
    >
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Background jobs are configured in config/*.json files
        </Typography>
        <Button
          onClick={fetchJobs}
          variant="outlined"
          startIcon={<RefreshIcon />}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scheduled Jobs ({jobs.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scheduled jobs running across the cluster
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Job Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Node</TableCell>
                  <TableCell>Last/Next Execution</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <React.Fragment key={job.job_name}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleJobExpansion(job.job_name)}
                        >
                          {expandedJobs[job.job_name] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {job.job_name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {job.requires_master && (
                              <Chip label="Master Only" color="warning" size="small" />
                            )}
                            {!job.enabled && (
                              <Chip label="Disabled" color="default" size="small" />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={job.job_type} variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>
                        {getStatusChip(job.current_status)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" component="div">
                          {job.schedule_type || 'interval'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.schedule_value || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {job.assigned_node || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Last: {formatTimestamp(job.last_execution)}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Next: {formatTimestamp(job.next_execution)}
                          </Typography>
                          {job.error_count > 0 && (
                            <Box>
                              <Typography variant="caption" color="error">
                                Errors: {job.error_count}
                              </Typography>
                              {job.last_error && (
                                <Tooltip title={job.last_error}>
                                  <Typography variant="caption" color="error" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {job.last_error}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={!job.enabled ? "Job is disabled but can be manually triggered" : "Manually trigger job execution"}>
                          <span>
                            <Button
                              onClick={() => triggerJob(job.job_name)}
                              variant="outlined"
                              size="small"
                              disabled={actionLoading === `trigger_${job.job_name}`}
                              startIcon={
                                actionLoading === `trigger_${job.job_name}`
                                  ? <CircularProgress size={16} />
                                  : <PlayIcon />
                              }
                            >
                              Trigger
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: expandedJobs[job.job_name] ? 1 : 0 }}>
                        <Collapse in={expandedJobs[job.job_name]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  Job Configuration:
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.paper' }}>
                                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify(jobDetails[job.job_name]?.config || {}, null, 2)}
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                {jobDetails[job.job_name]?.runtime_state && Object.keys(jobDetails[job.job_name].runtime_state).length > 0 && (
                                  <>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Runtime State:
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.paper' }}>
                                      <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {JSON.stringify(jobDetails[job.job_name].runtime_state, null, 2)}
                                      </Typography>
                                    </Paper>
                                  </>
                                )}
                              </Grid>
                            </Grid>

                            <Typography variant="h6" sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <HistoryIcon fontSize="small" />
                              Execution History
                            </Typography>
                            {jobDetails[job.job_name]?.execution_history?.length > 0 ? (
                              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Started</TableCell>
                                      <TableCell>Duration</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Node</TableCell>
                                      <TableCell>Results</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {jobDetails[job.job_name].execution_history.map((exec, idx) => (
                                      <TableRow key={exec.execution_id || idx}>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {formatTimestamp(exec.started_at)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {exec.duration_seconds ? `${exec.duration_seconds.toFixed(2)}s` : '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          {exec.status === 'completed' ? (
                                            <Chip icon={<CheckCircleIcon />} label="Success" color="success" size="small" />
                                          ) : exec.status === 'error' ? (
                                            <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" />
                                          ) : (
                                            <Chip label={exec.status} size="small" />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {exec.node_id || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          {exec.error ? (
                                            <Tooltip title={exec.error}>
                                              <Typography variant="caption" color="error" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {exec.error}
                                              </Typography>
                                            </Tooltip>
                                          ) : exec.result ? (
                                            <Typography variant="caption" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              {JSON.stringify(exec.result)}
                                            </Typography>
                                          ) : (
                                            '-'
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No execution history available
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </AdminPageWrapper>
  );
};

export default AdminBackgroundJobsPage;
